"use client";

import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Calculator, Check, ArrowRight, Info } from "lucide-react";

interface Option {
  id: string;
  label: string;
  price: number;
  description: string;
}

const serviceTypes: Option[] = [
  { id: "starter", label: "Starter Website", price: 250, description: "1-3 pages, basic SEO" },
  { id: "business", label: "Business Website", price: 600, description: "Up to 7 pages, admin panel" },
  { id: "pro", label: "Professional Website", price: 1200, description: "Custom design, animations" },
  { id: "webapp", label: "Custom Web App", price: 1500, description: "Full-stack application" },
];

const addOns: Option[] = [
  { id: "seo", label: "Advanced SEO", price: 300, description: "Technical audit & optimization" },
  { id: "speed", label: "Speed Optimization", price: 150, description: "Lightning-fast performance" },
  { id: "auth", label: "User Auth System", price: 400, description: "Secure login & roles" },
  { id: "api", label: "API Integration", price: 500, description: "Connect with 3rd party tools" },
  { id: "i18n", label: "Multi-language", price: 350, description: "Support for multiple locales" },
];

export function CostEstimator() {
  const [selectedType, setSelectedType] = useState<string>(serviceTypes[0].id);
  const [selectedAddOns, setSelectedAddOns] = useState<string[]>([]);

  const toggleAddOn = (id: string) => {
    setSelectedAddOns(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const total = useMemo(() => {
    const typePrice = serviceTypes.find(t => t.id === selectedType)?.price || 0;
    const addOnPrice = addOns
      .filter(a => selectedAddOns.includes(a.id))
      .reduce((sum, a) => sum + a.price, 0);
    return typePrice + addOnPrice;
  }, [selectedType, selectedAddOns]);

  return (
    <div className="max-w-5xl mx-auto bg-neutral-900/40 backdrop-blur-xl border border-neutral-800 rounded-[3rem] overflow-hidden shadow-2xl">
      <div className="grid lg:grid-cols-2">
        {/* Selection Area */}
        <div className="p-8 md:p-12 border-b lg:border-b-0 lg:border-r border-neutral-800">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-violet-600/20 flex items-center justify-center text-violet-400">
              <Calculator size={20} />
            </div>
            <h3 className="text-xl font-black uppercase tracking-tight text-white">Project Estimator</h3>
          </div>

          <div className="space-y-8">
            <div>
              <label className="text-[10px] font-black uppercase tracking-[0.3em] text-neutral-500 mb-4 block">Select Service Type</label>
              <div className="grid grid-cols-1 gap-3">
                {serviceTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setSelectedType(type.id)}
                    className={`flex items-center justify-between p-4 rounded-2xl border transition-all text-left group ${
                      selectedType === type.id 
                        ? "bg-violet-600/10 border-violet-500/50 text-white" 
                        : "bg-neutral-950/50 border-neutral-800 text-neutral-400 hover:border-neutral-700"
                    }`}
                  >
                    <div>
                      <p className="font-bold uppercase tracking-tight text-sm">{type.label}</p>
                      <p className="text-xs text-neutral-500">{type.description}</p>
                    </div>
                    <div className={`w-6 h-6 rounded-full border flex items-center justify-center transition-all ${
                      selectedType === type.id ? "bg-violet-500 border-violet-500" : "border-neutral-700"
                    }`}>
                      {selectedType === type.id && <Check size={14} className="text-white" />}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[10px] font-black uppercase tracking-[0.3em] text-neutral-500 mb-4 block">Add-on Features</label>
              <div className="grid grid-cols-1 gap-3">
                {addOns.map((addOn) => (
                  <button
                    key={addOn.id}
                    onClick={() => toggleAddOn(addOn.id)}
                    className={`flex items-center justify-between p-4 rounded-2xl border transition-all text-left group ${
                      selectedAddOns.includes(addOn.id)
                        ? "bg-fuchsia-600/10 border-fuchsia-500/50 text-white" 
                        : "bg-neutral-950/50 border-neutral-800 text-neutral-400 hover:border-neutral-700"
                    }`}
                  >
                    <div>
                      <p className="font-bold uppercase tracking-tight text-sm">{addOn.label}</p>
                      <p className="text-xs text-neutral-500">{addOn.description}</p>
                    </div>
                    <div className={`w-6 h-6 rounded-full border flex items-center justify-center transition-all ${
                      selectedAddOns.includes(addOn.id) ? "bg-fuchsia-500 border-fuchsia-500" : "border-neutral-700"
                    }`}>
                      {selectedAddOns.includes(addOn.id) && <Check size={14} className="text-white" />}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Result Area */}
        <div className="p-8 md:p-12 bg-neutral-950/50 flex flex-col justify-between relative overflow-hidden">
          <div className="absolute top-0 right-0 -mr-24 -mt-24 w-80 h-80 bg-violet-600/10 blur-3xl rounded-full" />
          
          <div className="relative z-10">
            <div className="mb-12">
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-neutral-500 mb-2">Estimated Investment</p>
              <div className="flex items-baseline gap-2">
                <span className="text-6xl md:text-8xl font-black italic tracking-tighter text-white">
                  {total}€
                </span>
                <span className="text-neutral-500 font-bold uppercase tracking-widest text-xs">Starting from</span>
              </div>
            </div>

            <div className="space-y-6 mb-12">
              <div className="flex items-start gap-4 p-4 bg-neutral-900/50 border border-neutral-800 rounded-2xl">
                <Info size={20} className="text-violet-400 flex-shrink-0 mt-1" />
                <p className="text-sm text-neutral-400 leading-relaxed">
                  This is an automated estimate based on standard requirements. Final pricing may vary depending on specific features and complexity.
                </p>
              </div>
              
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-sm text-neutral-300">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500" /> 40-50% deposit to start
                </li>
                <li className="flex items-center gap-3 text-sm text-neutral-300">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500" /> Full source code ownership
                </li>
                <li className="flex items-center gap-3 text-sm text-neutral-300">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500" /> 30 days post-launch support
                </li>
              </ul>
            </div>
          </div>

          <div className="relative z-10">
            <a
              href="/contact"
              className="group w-full bg-white text-black py-6 rounded-2xl font-black uppercase tracking-[0.3em] text-xs flex items-center justify-center gap-3 hover:bg-violet-600 hover:text-white transition-all duration-500 shadow-xl"
            >
              Request Detailed Quote <ArrowRight size={18} className="group-hover:translate-x-2 transition-transform" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}